import React, { useEffect, useState } from "react";
import { ReactComponent as BaseVector } from "../../../../icons/baricon.svg";
import { ReactComponent as SimulationVector } from "../../../../icons/simulation_vector.svg";
import { ReactComponent as OptimalVector } from "../../../../icons/optimal_vector.svg";
import { ReactComponent as DiffrenceVector } from "../../../../icons/diffrence_vector.svg";

import { ReactComponent as Info } from "../../../../icons/info-Simulator.svg";

interface CostItem {
  label: string;
  value: string;
  percentage?: string;
}

interface CostSection {
  dam_purchase_mwh: number;
  dam_sale_mwh: number;
  avg_deficit_mw: number;
  no_of_deficit: number;
  no_of_surplus: number;
  unit_procured_mwh: number;
}

interface DisplaySection {
  title: string;
  titleColor: string;
  iconColor: string;
  iconBgColor: string;
  items: CostItem[];
  Icon?: React.ElementType;
}

// Skeleton Loader Component
const SkeletonLoader = () => {
  return (
    <div className="flex gap-6 w-full">
      {[1, 2, 3, 4].map((section) => (
        <div
          key={section}
          className="bg-white border border-[#DFEAF2] rounded-2xl shadow-lg p-4 w-full h-full animate-pulse"
        >
          {/* Title Skeleton */}
          <div className="h-6 bg-gray-200 rounded w-32 mb-3"></div>

          {/* Items Skeleton */}
          <div className="space-y-3">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="flex items-start gap-4 p-4 mb-2 bg-white border border-[#DFEAF2] shadow-sm rounded-xl"
              >
                {/* Icon Skeleton */}
                <div className="bg-gray-200 rounded-full w-9 h-9 flex-shrink-0"></div>

                {/* Content Skeleton */}
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="h-4 bg-gray-200 rounded w-40"></div>
                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function GnaStatisticsVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading,
  data,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  loading: boolean;
  data: CostSection[];
}) {
  const [costData, setCostData] = useState<DisplaySection[]>([]);

  // Format number with commas and 2 decimal places
  const formatNumber = (num: number): string => {
    return num.toLocaleString("en-IN", {
      // minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  useEffect(() => {
    if (data && data.length >= 2) {
      const current = data[0];
      const Simulated = data[1];
      const optimised = data[2];
      const difference = data[3];

      const formattedData: DisplaySection[] = [
        {
          title: "Base",
          titleColor: "text-[#819ACC]",
          iconColor: "text-[#819ACC]",
          Icon: BaseVector,
          iconBgColor: "bg-blue-100",
          items: [
            {
              label: "Total DAM Purchase (MWh)",
              value: formatNumber(current.dam_purchase_mwh),
            },
            {
              label: "Total DAM Sale (MWh)",
              value: formatNumber(current.dam_sale_mwh),
            },
            {
              label: "Avg Surplus/Deficit (MW)",
              value: formatNumber(current.avg_deficit_mw),
            },
            {
              label: "No. of Deficit Slots",
              value: formatNumber(current.no_of_deficit),
            },
            {
              label: "No. of Surplus Slots",
              value: formatNumber(current.no_of_surplus),
            },
            {
              label: "Base Total Units Procured (MU)",
              value: formatNumber(current.unit_procured_mwh),
            },
          ],
        },
        {
          title: "Simulated",
          titleColor: "text-[#F1935C]",
          iconColor: "text-teal-500",
          iconBgColor: "bg-orange-100",
          Icon: SimulationVector,
          items: [
            {
              label: "Total DAM Purchase (MWh)",
              value: formatNumber(Simulated.dam_purchase_mwh),
            },
            {
              label: "Total DAM Sale (MWh)",
              value: formatNumber(Simulated.dam_sale_mwh),
            },
            {
              label: "Avg Surplus/Deficit (MW)",
              value: formatNumber(Simulated.avg_deficit_mw),
            },
            {
              label: "No. of Deficit Slots",
              value: formatNumber(Simulated.no_of_deficit),
            },
            {
              label: "No. of Surplus Slots",
              value: formatNumber(Simulated.no_of_surplus),
            },
            {
              label: "Simulated Total Units Procured (MU)",
              value: formatNumber(Simulated.unit_procured_mwh),
            },
          ],
        },
        {
          title: "Optimal",
          titleColor: "text-[#34656D]",
          iconColor: "text-orange-500",
          iconBgColor: "bg-[#E2F6F6]",
          Icon: OptimalVector,
          items: [
            {
              label: "Total DAM Purchase (MWh)",
              value: formatNumber(optimised.dam_purchase_mwh),
            },
            {
              label: "Total DAM Sale (MWh)",
              value: formatNumber(optimised.dam_sale_mwh),
            },
            {
              label: "Avg Surplus/Deficit (MW)",
              value: formatNumber(optimised.avg_deficit_mw),
            },
            {
              label: "No. of Deficit Slots",
              value: formatNumber(optimised.no_of_deficit),
            },
            {
              label: "No. of Surplus Slots",
              value: formatNumber(optimised.no_of_surplus),
            },
            {
              label: "Optimal Total Units Procured (MU)",
              value: formatNumber(optimised.unit_procured_mwh),
            },
          ],
        },
        {
          title: "Difference (Optimal vs Base)",
          titleColor: "text-[#FF1028]",
          iconColor: "text-orange-500",
          iconBgColor: "bg-red-100",
          Icon: DiffrenceVector,
          items: [
            {
              label: "Total DAM Purchase (MWh)",
              value: formatNumber(difference.dam_purchase_mwh),
            },
            {
              label: "Total DAM Sale (MWh)",
              value: formatNumber(difference.dam_sale_mwh),
            },
            {
              label: "Avg Surplus/Deficit (MW)",
              value: formatNumber(difference.avg_deficit_mw),
            },
            {
              label: "No. of Deficit Slots",
              value: formatNumber(difference.no_of_deficit),
            },
            {
              label: "No. of Surplus Slots",
              value: formatNumber(difference.no_of_surplus),
            },
            {
              label: "Change in Total Units Procured (MU)",
              value: formatNumber(difference.unit_procured_mwh),
            },
          ],
        },
      ];

      setCostData(formattedData);
    }
  }, [data, refreshKey]);

  return (
    <>
      {loading ? (
        <div className="w-full !overflow-auto !h-[calc(100vh-560px)]">
          <div className="w-full pb-2">
            <div className="flex flex-nowrap justify-around gap-6 w-full">
              <SkeletonLoader />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full !overflow-auto !h-[calc(100vh-520px)]">
          <div className="w-full pb-2">
            <div className="flex flex-nowrap justify-around gap-6 w-full">
              {costData.length > 0 ? (
                <div className="flex gap-6 w-full">
                  {costData.map((section, sectionIndex) => {
                    const SectionIcon = section.Icon;
                    return (
                      <div
                      key={sectionIndex}
                      className="bg-white border border-[#DFEAF2] rounded-2xl shadow-lg p-4 w-full h-full"
                    >
                      {/* Section Title */}
                      <h2
                        className={`text-base font-medium mb-3 ${section.titleColor}`}
                      >
                        {section.title}
                      </h2>

                      {/* Cost Items */}
                      <div className="space-y-3">
                        {section.items.map((item, itemIndex) => (
                          <div
                            key={itemIndex}
                            className="flex items-start gap-4 p-4 mb-2 bg-white border border-[#DFEAF2] shadow-sm rounded-xl"
                          >
                            {/* Icon */}
                            <div
                              className={`${section.iconBgColor} rounded-full p-2 flex-shrink-0`}
                            >
                              {SectionIcon ? (
                                <SectionIcon
                                  className={`w-4 h-4 ${section.iconColor}`}
                                />
                              ) : (
                                <BaseVector
                                  className={`w-4 h-4 ${section.iconColor}`}
                                />
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm text-[#819ACC] font-medium">
                                  {item.label}
                                </span>
                                {/* <Info className="w-4 h-4 text-slate-300" /> */}
                              </div>
                              <div className="flex items-baseline gap-2">
                                <span className="text-lg font-medium text-[#525062]">
                                  {item.value}
                                </span>
                                {item.percentage && (
                                  <span className="text-lg font-semibold text-[#2B3674]">
                                    {item.percentage}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8 w-full">
                  No Data Available...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}